import AccessTime from '@mui/icons-material/AccessTime';
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import MenuBookOutlined from '@mui/icons-material/MenuBookOutlined';
import { Box, Button, Stack, Typography } from '@mui/material';
import {
  courseTypeIconNames,
  modalityIconMap,
  resourceIconMap,
} from '@/constants/resources/resources-icons';
import {
  courseTypesLabels,
  modalityTypesLabels,
} from '@/constants/catalog/course';
import type { DurationTypes } from '@/models/catalog/course';
import { EmbeddedCourseSource } from '@/models/learning/academic-situation';
import { formatDuration } from '@/utils/masks/time';
import type { AcademicSituationCardProps } from './interfaces';
import { SituationOptionsMenu } from './situation-options-menu';
import { StatusChipDropdown } from './status-chip-dropdown';
import { academicStatusConfig } from './status-config';

const formatStartedAt = (startedAt: string): string => {
  const label = new Date(startedAt).toLocaleDateString('pt-BR', {
    month: 'short',
    year: 'numeric',
    timeZone: 'America/Sao_Paulo',
  });

  return `Iniciado em ${label}`;
};

const MetaItem = ({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={0.5}
    color="neutral.500"
  >
    {icon}
    <Typography variant="body1">{children}</Typography>
  </Stack>
);

export const AcademicSituationCard = ({
  situation,
  href,
  onClick,
  onDelete,
}: AcademicSituationCardProps) => {
  const { current_status, started_at, embedded_course } = situation;
  const status = academicStatusConfig[current_status];
  const isManual =
    embedded_course.source === EmbeddedCourseSource.ManualInsertion;

  const CourseTypeIcon =
    resourceIconMap[courseTypeIconNames[embedded_course.course_type]];

  return (
    <Box
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        border: ({ palette }) =>
          `1px solid ${status.banner ? palette[status.palette].main : palette.background.default}`,
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ md: 'center' }}
        spacing={{ xs: 2, md: 3 }}
        p={3}
        sx={{ backgroundColor: 'background.paper' }}
      >
        <Stack
          height={72}
          width={72}
          flexShrink={0}
          alignItems="center"
          justifyContent="center"
          sx={{
            borderRadius: 1,
            overflow: 'hidden',
            backgroundColor:
              embedded_course.external_institution_background_color ??
              'background.default',
          }}
        >
          {embedded_course.external_institution_logo_url ? (
            <Box
              component="img"
              src={embedded_course.external_institution_logo_url}
              alt={embedded_course.institution_name ?? ''}
              sx={{ maxHeight: 72, maxWidth: 72, objectFit: 'contain' }}
            />
          ) : (
            <MenuBookOutlined sx={{ fontSize: 32, color: 'neutral.500' }} />
          )}
        </Stack>

        <Stack flex={1} spacing={1} minWidth={0}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row-reverse' },
              justifyContent: { md: 'flex-end' },
              alignItems: { xs: 'flex-start', md: 'center' },
              gap: 1.5,
            }}
          >
            <Box sx={{ flexShrink: 0 }}>
              <StatusChipDropdown situation={situation} />
            </Box>
            <Typography variant="h2">{embedded_course.name}</Typography>
          </Box>

          <Stack
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            rowGap={1}
            columnGap={2}
          >
            {CourseTypeIcon && (
              <MetaItem icon={<CourseTypeIcon fontSize="small" />}>
                {courseTypesLabels[embedded_course.course_type]}
              </MetaItem>
            )}
            {embedded_course.duration && embedded_course.duration_type && (
              <MetaItem icon={<AccessTime fontSize="small" />}>
                {formatDuration(
                  embedded_course.duration_type as DurationTypes,
                  embedded_course.duration
                )}
              </MetaItem>
            )}
            <MetaItem icon={modalityIconMap[embedded_course.modality]}>
              {modalityTypesLabels[embedded_course.modality]}
            </MetaItem>
            {started_at && (
              <MetaItem icon={<CalendarTodayOutlined fontSize="small" />}>
                {formatStartedAt(started_at)}
              </MetaItem>
            )}
          </Stack>
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          flexShrink={0}
          alignItems="center"
          alignSelf={{ xs: 'stretch', md: 'center' }}
          sx={{ mt: { xs: 1, md: 0 } }}
        >
          {status.action && (
            <Button
              variant={status.action.variant}
              href={href}
              onClick={() =>
                onClick?.({ expandForm: status.action?.expandForm })
              }
              sx={{ flexGrow: { xs: 1, md: 0 } }}
            >
              {status.action.label}
            </Button>
          )}
          {isManual && <SituationOptionsMenu onDelete={onDelete} />}
        </Stack>
      </Stack>

      {status.banner && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          px={3}
          py={1.5}
          sx={{
            backgroundColor: status.backgroundColor,
            color: status.color,
          }}
        >
          <ErrorOutline sx={{ fontSize: 18 }} />
          <Typography
            color={status.color}
            sx={(theme) => ({
              fontFamily: theme.typography.h2.fontFamily,
              fontWeight: 600,
              fontSize: 13,
              lineHeight: 1.5,
            })}
          >
            {status.banner}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};
